import React, { useContext, useState, useEffect, ChangeEvent } from 'react';
import { IconButton, Box, Container, Toolbar, AppBar } from '@mui/material';
import ProfileDetailsInput from '../../../components/Inputs/ProfileDetailsInput';
import Sidebar from '../../../components/ConsumerSidebar/ConsumerSidebar';
import './CreateTicket.css';
import TemporaryDrawer from '../../../components/TemporaryDrawer/TemporaryDrawer';
import {
  ProfileContext,
  ProfileContextProps,
} from '../../../context/ProfileContext';
import Textarea from '@mui/joy/Textarea';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import { styled } from '@mui/joy/styles';
import UploadButtonIcon from '../../../assets/UploadButtonIcon.svg';
import StyledButton from '../../../components/Button/Button';
import { createTicket } from '../../../services/NetworkServices';
import { getStage } from '../../../services/NetworkServices';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import Popup from '../../../components/Inputs/Popup';
import { useNavigate } from 'react-router-dom';
import LogoutPopup from '../../../components/Inputs/LogoutPopup';
import LeftArrow from '../../../assets/Left 1.svg';
import EmailIcon from '../../../assets/Message 36.svg';
import ApiStatus from '../../../config/apiStatusConstants';
import CircularProgress from '@mui/material/CircularProgress';

const StyledTextarea = styled(TextareaAutosize)({
  resize: 'none',
  border: 'none',
  minWidth: 0,
  outline: 0,
  padding: 0,
  paddingBlockStart: '1em',
  paddingTop: '2.7rem',
  paddingLeft: '2.2rem',
  paddingInlineEnd: `var(--Textarea-paddingInline)`,
  flex: 'auto',
  alignSelf: 'stretch',
  color: '#FFFFFC',
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontStyle: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  '&:focus': {
    borderColor: 'transparent', // Ensure border remains transparent on focus
    outline: 'none', // Remove any outline on focus
  },
  '&:hover': {
    borderColor: 'transparent', // Ensure no border color on hover
  },
  '&:active': {
    borderColor: 'transparent', // Ensure no border color on active state
  },
  '&:focus-visible': {
    borderColor: 'transparent', // Disable focus-visible outline
  },
  '&::placeholder': {
    opacity: 0,
    transition: '0.1s ease-out',
  },
  '&:focus::placeholder': {
    opacity: 1,
  },

  '&:focus + textarea + label, &:not(:placeholder-shown) + textarea + label': {
    top: '1.5rem',
    left: '2.8rem',
  },
  '&:focus + textarea + label': {
    color: 'var(--Textarea-focusedHighlight)',
  },
});

const StyledLabel = styled('label')(({ theme }) => ({
  position: 'absolute',
  lineHeight: 1,
  top: 'calc((var(--Textarea-minHeight) - 1em) / 2)',
  color: theme.vars.palette.text.tertiary,
  fontWeight: theme.vars.fontWeight.md,
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
}));

const InnerTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentPropsWithoutRef<'textarea'>
>(function InnerTextarea(props, ref) {
  const id = React.useId();
  return (
    <React.Fragment>
      <StyledTextarea minRows={2} {...props} ref={ref} id={id} />
      <StyledLabel htmlFor={id} color="#7D7D7D">
        <strong>Enter Description</strong>
      </StyledLabel>
    </React.Fragment>
  );
});

const CreateTicket: React.FC = () => {
  const { userProfile, showLogoutPopup } = useContext(
    ProfileContext,
  ) as ProfileContextProps;
  console.log('user profile in create ticket is ', userProfile);
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const navigate = useNavigate();

  const formatPhoneNumber = (input: string | undefined) => {
    if (!input) return ''; // Handle undefined or empty input

    // Remove all non-numeric characters
    const numbersOnly = input.replace(/\D/g, '');

    // Format as XXX XXXX XXX
    const formattedNumber = numbersOnly.replace(
      /(\d{3})(\d{0,4})(\d{0,3})/,
      '$1-$2-$3',
    );

    // Trim the result to remove any trailing spaces
    return formattedNumber.trim();
  };

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        const response = await getStage();
        const data = await response.data;
        if (data) {
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/participant/createProfile');
          }
          if (stage === '2') {
            navigate('/participant/inviteBabysitter');
          }
          if (stage === '3') {
            navigate('/participant/selectplan');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleContinue = () => {
    setShowPopup(false);
    setDescription('');
    setSelectedFile(null);
    setPreview(null);
  };

  const handleDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };

  const handleTicketCreation = async () => {
    setApiStatus(ApiStatus.LOADING);
    const formData = new FormData();
    formData.append('description', description);
    formData.append('platform', 'web');
    if (selectedFile) {
      formData.append('supportQueryImage', selectedFile);
    }
    try {
      const response = await createTicket(formData);
      const data = await response.data;
      if (data) {
        setApiStatus(ApiStatus.SUCCESS);
        setShowPopup(true);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg(
        error.response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    }
  };

  const handleBackClick = () => {
    navigate('/participant/dashboard');
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <Box sx={{ display: 'flex', alignSelf: 'left' }}>
          <StyledButton sx={{ marginTop: '7vh' }}>
            <CircularProgress />
          </StyledButton>
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', alignSelf: 'left' }}>
          <StyledButton
            onClick={handleTicketCreation}
            sx={{ marginTop: '7vh' }}
            disabled={!description}
          >
            Submit
          </StyledButton>
        </Box>
      );
    }
  };

  const renderErrorMsg = () => {
    if (apiStatus === ApiStatus.FAILURE) {
      return <p className="error-msg">{errorMsg}</p>;
    }
    return null;
  };

  return (
    <>
      {showPopup && <div className="backdrop"></div>}
      <Box className={`create-ticket ${showLogoutPopup ? 'popup-active' : ''}`}>
        <Box className="account-details">
          <Box
            sx={{
              display: { xs: 'block', sm: 'flex' },
              height: { xs: '100vh', sm: '100vh' },
              overflow: { xs: 'auto', sm: 'hidden' },
            }}
          >
            <AppBar
              position="static"
              sx={{
                display: { xs: 'block', sm: 'none' },
                backgroundColor: 'black',
              }}
            >
              <Toolbar sx={{ background: 'black' }}>
                <TemporaryDrawer />
              </Toolbar>
            </AppBar>

            {/* Sidebar */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: { xs: '0%', sm: '35%', md: '28%' },
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  marginLeft: { sm: 2, md: 14 },
                  marginTop: { sm: 2, md: 4 },
                  marginBottom: { sm: 30, md: 4 },
                }}
              >
                <Sidebar />
              </Box>
            </Box>

            {/* Main Content */}
            <Box
              sx={{
                width: { xs: '100%', sm: '65%', md: '72%' },
                flexGrow: 1,
                overflow: 'auto',
                height: '100%',
                paddingY: 4,
              }}
            >
              <Container>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <img
                    src={LeftArrow}
                    onClick={handleBackClick}
                    className="arrow-sm"
                    alt="Back Arrow"
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column', // column layout on xs and sm screens
                      sm: 'column',
                      md: 'row', // row layout on md and larger screens
                    },
                    gap: '20px',
                    paddingTop: '60px',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <ProfileDetailsInput
                      type="text"
                      label="Full Name"
                      value={
                        userProfile?.firstName + ' ' + userProfile?.lastName
                      }
                      name="fullName"
                    />

                    <ProfileDetailsInput
                      type="text"
                      label="Phone Number"
                      value={formatPhoneNumber(userProfile?.phone?.toString())}
                      name="phone"
                    />

                    <Textarea
                      slots={{ textarea: InnerTextarea }}
                      value={description}
                      sx={{
                        borderRadius: '40px',
                        // Responsive width
                        marginLeft: '7px',
                        height: '30vh',
                        borderColor: '#365F91',
                        border: '1.4px solid transparent',
                        background: `
                        linear-gradient(black, black) padding-box,
                        linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box
                      `,
                        borderImageSlice: 1,
                        '&:active': {
                          borderColor: 'none',
                        },
                        '&:focus-visible': {
                          outline: 'none',
                          borderColor: 'none',
                        },
                      }}
                      onChange={handleDescription}
                    />
                    {renderButton()}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <ProfileDetailsInput
                      type="text"
                      label="Email Address"
                      value={userProfile?.userId?.email}
                      name="email"
                      isIconPresent
                      startIcon={
                        <img
                          src={EmailIcon}
                          alt="Email"
                          className="email-icon"
                        />
                      }
                    />
                    <Box className="upload-query-container bordergrade">
                      <input
                        accept="image/*"
                        className="support-query-image-input"
                        id="upload-front-file"
                        type="file"
                        name="idFront"
                        onChange={handleChange}
                      />
                      {!preview && (
                        <>
                          <label htmlFor="upload-front-file">
                            <IconButton
                              color="primary"
                              aria-label="upload government id"
                              component="span"
                            >
                              <img src={UploadButtonIcon} alt="Upload" />
                            </IconButton>
                          </label>
                          <p className="upload-text">Upload</p>
                        </>
                      )}

                      {preview && (
                        <img
                          src={preview}
                          alt="Profile Preview"
                          className="ticket-image-preview"
                        />
                      )}

                      {showPopup && (
                        <Popup
                          title={
                            <>
                              Report Submitted <br /> Successfully
                            </>
                          }
                          message={
                            <>
                              Your question has been sent to a member of our
                              team and is being dealt with as a priority.
                            </>
                          }
                          buttonText="Done"
                          onButtonClick={handleContinue}
                        />
                      )}

                      {showLogoutPopup && <LogoutPopup />}
                    </Box>
                  </Box>
                </Box>
              </Container>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CreateTicket;
